import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { WalletProvider, useWallet } from './context/WalletContext';
import Sidebar from './components/layout/Sidebar';
import MobileDrawer from './components/layout/mobiledrawer';
import Topbar from './components/layout/Topbar';
import Dashboard from './pages/user/Dashboard';
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import ReferralPromptModal from './components/common/ReferralPromptModal';
import { useDolphin } from './hooks/useDolphin';
import './styles/global.css';

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};


const AppLayout = () => {
      // Referral modal state for loading and error
      const [registering, setRegistering] = useState(false);
      const [registerError, setRegisterError] = useState('');
    // Referral modal submit handler (must be at top level)
    const handleReferralSubmit = async ({ inviteCode, skip }) => {
      setRegisterError('');
      setRegistering(true);
      if (skip) {
        setReferralPromptStatus(account, 'skipped');
        setShowReferralModal(false);
        setRegistering(false);
        return;
      }
      if (inviteCode) {
        try {
          await setUserReferrer(inviteCode);
          setReferralPromptStatus(account, 'completed');
          setShowReferralModal(false);
          setRegistering(false);
        } catch (err) {
          // Handle RSET revert (already has referrer)
          const isRSET =
            (err?.message && err.message.includes('RSET')) ||
            (err?.data?.originalError?.message && err.data.originalError.message.includes('RSET'));
          const errorMsg = err?.message || err?.data?.originalError?.message || JSON.stringify(err);
          if (isRSET) {
            setReferralPromptStatus(account, 'completed');
            setShowReferralModal(false);
            setRegistering(false);
            setRegisterError('You already have a referrer set for this wallet.');
          } else {
            setRegisterError(errorMsg || 'Failed to set referrer');
            setRegistering(false);
          }
        }
      } else {
        setRegisterError('Please enter a referral code or skip.');
        setRegistering(false);
      }
    };
  // Referral modal state must be declared before any use
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [initialInviteCode, setInitialInviteCode] = useState('');

  useEffect(() => {
    if (showReferralModal) {
      const urlParams = new URLSearchParams(window.location.search);
      const refParam = urlParams.get('ref') || '';
      setInitialInviteCode(refParam);
    }
  }, [showReferralModal]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isConnected, account } = useWallet();
  const isMobile = useIsMobile();
  const { referralStats, dolphinContract, setUserReferrer } = useDolphin();
  const [referralCheckDone, setReferralCheckDone] = useState(false);
  const [lastCheckedAccount, setLastCheckedAccount] = useState(null);

  // International-style: track per-wallet referral prompt status
  const getReferralPromptStatus = (address) => {
    if (!address) return null;
    return localStorage.getItem(`referralPromptStatus_${address.toLowerCase()}`);
  };
  const setReferralPromptStatus = (address, status) => {
    if (!address) return;
    localStorage.setItem(`referralPromptStatus_${address.toLowerCase()}`, status);
  };


  // ...existing code...

  useEffect(() => {
    console.log('[ReferralCheck] useEffect fired:', {
      isConnected,
      account,
      dolphinContractReady: !!dolphinContract,
      referralStats,
    });
    // International-style referral prompt logic
    if (localStorage.getItem('referralJustSet') === 'true') {
      setShowReferralModal(false);
      setReferralCheckDone(true);
      localStorage.removeItem('referralJustSet');
      if (account) setReferralPromptStatus(account, 'completed');
      return;
    }
    if (isConnected && account && dolphinContract) {
      if (lastCheckedAccount !== account) {
        setReferralCheckDone(false);
        setShowReferralModal(false);
        setLastCheckedAccount(account);
        // Wait for referralStats to update for new account
        return;
      }
      if (!referralStats) {
        setReferralCheckDone(false);
        return;
      }
      const hasReferrer = !!(referralStats.hasReferrer || (referralStats.referrer && referralStats.referrer !== '0x0000000000000000000000000000000000000000'));
      const promptStatus = getReferralPromptStatus(account);
      if (hasReferrer) {
        setReferralPromptStatus(account, 'completed');
        setShowReferralModal(false);
        setReferralCheckDone(true);
      } else if (promptStatus === 'skipped') {
        setShowReferralModal(false);
        setReferralCheckDone(true);
      } else if (promptStatus === 'completed') {
        setShowReferralModal(false);
        setReferralCheckDone(true);
      } else {
        setShowReferralModal(true);
        setReferralCheckDone(true);
      }
    } else if (!isConnected) {
      setShowReferralModal(false);
      setReferralCheckDone(false);
      setLastCheckedAccount(null);
    } else {
      setReferralCheckDone(false);
    }
  }, [isConnected, account, referralStats, dolphinContract, lastCheckedAccount]);

  // Auto-close mobile drawer when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileOpen) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileOpen]);


  // Show landing page when not connected
  if (!isConnected) {
    return <LandingPage />;
  }

  // Block all routes except landing until referral check is done
  if (!referralCheckDone) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-cyan-500"></div>
      </div>
    );
  }



  // ...existing code...

  // Show the referral prompt modal as a true modal overlay (not a route)
  // Block interaction with the app until user completes or skips
  // Only show if user does NOT have a referrer
  const hasReferrer = !!(referralStats && (referralStats.hasReferrer || (referralStats.referrer && referralStats.referrer !== '0x0000000000000000000000000000000000000000')));
  const showModal = showReferralModal && account && dolphinContract && referralStats && !hasReferrer;

  return (
    <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Referral Prompt Modal as overlay */}
      {showModal && (
        <ReferralPromptModal
          open={true}
          initialInviteCode={initialInviteCode}
          onSubmit={handleReferralSubmit}
          registering={registering}
          registerError={registerError}
          account={account}
          dolphinContract={dolphinContract}
        />
      )}
      {/* Mobile Drawer */}
      <MobileDrawer 
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar 
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
      <div className="main-wrapper">
        {/* Topbar - contains mobile menu button */}
        <Topbar 
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard activeTab={activeTab} setActiveTab={setActiveTab} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <WalletProvider>
      <Router>
        <Routes>
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/*" element={<AppLayout />} />
        </Routes>
        <ToastContainer 
          position="top-right" 
          autoClose={4000} 
          hideProgressBar={false} 
          newestOnTop 
          closeOnClick 
          pauseOnFocusLoss 
          draggable 
          pauseOnHover 
        />
      </Router>
    </WalletProvider>
  );
}

export default App;